import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { useRelanceDeactivationStore } from '../../stores/relanceDeactivationStore';
import { useToastStore } from '../../stores/toastStore';
import { formatSnoozeDate, formatCurrency } from '../../utils/formatters';
import { ReactivateRelanceModal } from '../../features/relance/ReactivateRelanceModal';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import {
    SYSTEM_ACTOR,
    AUTO_REASON_SNOOZE_EXPIRED,
    AUTO_REASON_DEBT_INCREASED,
    AUTO_REASON_DEBT_CHANGED,
    type HistoryEventItemDto,
} from '../../services/eventHistoryService';

interface HistoryExpandedDetailsProps {
    event: HistoryEventItemDto;
    locale: string;
    className?: string;
    onReactivated?: () => void;
}

export const HistoryExpandedDetails: React.FC<HistoryExpandedDetailsProps> = ({ event, locale, className, onReactivated }) => {
    const { t } = useTranslation();
    const reactivateTask = useRelanceDeactivationStore((s) => s.reactivateTask);
    const showToast = useToastStore((s) => s.showToast);
    const [reactivateModalOpen, setReactivateModalOpen] = useState(false);

    const isRelance = event.type === 'RELANCE_COMPLETED';
    const isDeactivated = event.type === 'RELANCE_DEACTIVATED';
    const isReactivated = event.type === 'RELANCE_REACTIVATED';
    const isPaid = event.type === 'RELANCE_PAID';
    const isPaymentsApplied = event.type === 'TRANSPHERE_PAYMENTS_APPLIED';

    const performedBy =
        event.performedBy === SYSTEM_ACTOR
            ? t(TranslationKey.History.Detail.SystemActor)
            : event.performedBy ?? t(TranslationKey.History.Detail.UnknownUser);

    // Automatic reactivations store a language-neutral sentinel reason; localize it per viewer.
    // Manual reactivations carry the user's free text (shown as-is), or none.
    const localizeReason = (reason: string | null): string => {
        if (reason === AUTO_REASON_SNOOZE_EXPIRED) return t(TranslationKey.History.Detail.AutoReasonSnoozeExpired);
        if (reason === AUTO_REASON_DEBT_INCREASED) return t(TranslationKey.History.Detail.AutoReasonDebtIncreased);
        if (reason === AUTO_REASON_DEBT_CHANGED) return t(TranslationKey.History.Detail.AutoReasonDebtChanged);
        return reason ?? t(TranslationKey.History.Detail.None);
    };

    const handleReactivate = async (reason: string | null) => {
        if (!isDeactivated || !event.relanceId) return;
        if (!(await reactivateTask(event.relanceId, reason))) {
            showToast('error', t(TranslationKey.ApiErrors.Generic));
            return;
        }
        setReactivateModalOpen(false);
        onReactivated?.();
    };

    return (
        <div className={`py-m bg-surface border-t border-line-subtle ${className ? ` ${className}` : ''}`} data-testid="history-event-details">
            <div className="flex flex-col gap-0 text-s font-normal text-content leading-7 min-w-0">
                <div className="flex gap-m">
                    <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.PerformedBy)} :</span>
                    <span className="truncate min-w-0" title={performedBy} data-testid="history-detail-performed-by">{performedBy}</span>
                </div>

                {isRelance && (
                    <>
                        {event.recipientEmail && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.RecipientAddress)} :</span>
                                <span className="truncate min-w-0" title={event.recipientEmail} data-testid="history-detail-recipient">{event.recipientEmail}</span>
                            </div>
                        )}
                        {event.ccEmail && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.CcAddress)} :</span>
                                <span className="truncate min-w-0" title={event.ccEmail} data-testid="history-detail-cc">{event.ccEmail}</span>
                            </div>
                        )}
                        {event.bccEmail && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.BccAddress)} :</span>
                                <span className="truncate min-w-0" title={event.bccEmail} data-testid="history-detail-bcc">{event.bccEmail}</span>
                            </div>
                        )}
                        {event.replyToEmail && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.ReplyToAddress)} :</span>
                                <span className="truncate min-w-0" title={event.replyToEmail} data-testid="history-detail-reply-to">{event.replyToEmail}</span>
                            </div>
                        )}
                        {event.subject && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.Subject)} :</span>
                                <span className="truncate min-w-0" title={event.subject} data-testid="history-detail-subject">{event.subject}</span>
                            </div>
                        )}
                        {event.attachmentCount > 0 && (
                            <div className="flex gap-m">
                                <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.AttachmentCount)} :</span>
                                <span className="truncate min-w-0" data-testid="history-detail-attachment-count">{event.attachmentCount}</span>
                            </div>
                        )}
                    </>
                )}

                {isDeactivated && (
                    <>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.SnoozeUntil)} :</span>
                            <span className="truncate min-w-0" data-testid="history-detail-snooze-until">
                                {event.snoozeUntil
                                    ? formatSnoozeDate(event.snoozeUntil)
                                    : t(TranslationKey.Home.Snooze.Indefinite)}
                            </span>
                        </div>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.Note)} :</span>
                            <span className="truncate min-w-0" title={event.note ?? undefined} data-testid="history-detail-note">
                                {event.note ?? t(TranslationKey.History.Detail.None)}
                            </span>
                        </div>
                        {event.relanceId && event.relanceIsActive === false && (
                            <LinkButton
                                onClick={() => setReactivateModalOpen(true)}
                                className="self-start"
                                data-testid="history-detail-reactivate-link"
                            >
                                {t(TranslationKey.History.Detail.Reactivate)}
                            </LinkButton>
                        )}
                    </>
                )}

                {isReactivated && (
                    <div className="flex gap-m">
                        <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.Reason)} :</span>
                        <span className="truncate min-w-0" title={localizeReason(event.reason)} data-testid="history-detail-reason">
                            {localizeReason(event.reason)}
                        </span>
                    </div>
                )}

                {isPaid && (
                    <>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.AmountReceived)} :</span>
                            <span className="truncate min-w-0" data-testid="history-detail-amount-received">
                                {formatCurrency(event.amountReceived, locale)}
                            </span>
                        </div>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.RemainingBalance)} :</span>
                            <span className="truncate min-w-0" data-testid="history-detail-remaining-balance">
                                {formatCurrency(event.remainingBalance, locale)}
                            </span>
                        </div>
                    </>
                )}

                {isPaymentsApplied && (
                    <>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.InvoicesApplied)} :</span>
                            <span className="truncate min-w-0" data-testid="history-detail-invoices-applied">{event.invoicesApplied}</span>
                        </div>
                        <div className="flex gap-m">
                            <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.CustomersUpdated)} :</span>
                            <span className="truncate min-w-0" data-testid="history-detail-customers-updated">{event.customersUpdated}</span>
                        </div>
                    </>
                )}

                {event.type === 'NOTE_ADDED' && event.noteContent && (
                    <div className="flex gap-m">
                        <span className="w-32 shrink-0 whitespace-nowrap">{t(TranslationKey.History.Detail.NoteContent)} :</span>
                        <span className="truncate min-w-0" title={event.noteContent} data-testid="history-detail-note-content">{event.noteContent}</span>
                    </div>
                )}
            </div>

            {reactivateModalOpen && (
                <ReactivateRelanceModal
                    onCancel={() => setReactivateModalOpen(false)}
                    onConfirm={handleReactivate}
                />
            )}
        </div>
    );
};
